/* ============================================================================
 * clientVault.js — the "Master Vault": one chronological timeline of every
 * interaction with a contact, stitched from data we already store.
 *
 * Sources (all existing — no new tracking):
 *   - the contact row itself (created, current status + when)
 *   - estimates (by client_id): created + sent
 *   - portal signatures / messages / photos (settings, keyed by client_id)
 *   - jobs linked to the contact
 *
 * Returns events newest-first: [{ id, kind, title, detail, at }].
 * `kind` drives the icon/tone in the UI (see ContactProfileSheet VAULT_ICONS).
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';
import {
  fetchPortalSignatures,
  fetchPortalMessages,
  fetchPortalPhotos,
} from '@/lib/clientPortalData';
import { findJobsForContact, getLeadStatus, LEAD_STATUS_META } from '@/lib/clientsData';

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const clip = (s, n = 90) => {
  const str = String(s || '').replace(/\s+/g, ' ').trim();
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
};

export const gatherClientVault = async (contact, jobs = []) => {
  const clientId = contact?.id;
  if (!clientId) return [];
  const events = [];

  // Contact created + current status.
  if (contact.created_at) {
    events.push({
      id: `created-${clientId}`,
      kind: 'created',
      title: contact.type === 'client' ? 'Client added' : 'Lead created',
      detail: contact.source ? `Source: ${contact.source}` : '',
      at: contact.created_at,
    });
  }
  const status = getLeadStatus(contact);
  if (contact.lead_status_at) {
    events.push({
      id: `status-${clientId}`,
      kind: 'status',
      title: `Marked ${LEAD_STATUS_META[status]?.label || cap(status)}`,
      detail: '',
      at: contact.lead_status_at,
    });
  }

  // Estimates (created + sent).
  try {
    const { data: ests } = await supabase
      .from('estimates')
      .select('id,title,status,total,created_at,sent_at')
      .eq('client_id', clientId);
    for (const e of ests || []) {
      events.push({
        id: `est-${e.id}`,
        kind: 'estimate',
        title: `Estimate: ${e.title || 'Untitled'}`,
        detail: cap(e.status || 'draft'),
        at: e.created_at,
      });
      if (e.sent_at) {
        events.push({
          id: `est-sent-${e.id}`,
          kind: 'sent',
          title: `Sent: ${e.title || 'Estimate'}`,
          detail: '',
          at: e.sent_at,
        });
      }
    }
  } catch {
    /* estimates unavailable — skip */
  }

  // Signatures / messages / photos (settings, keyed by client_id).
  let sigs = {};
  let msgs = {};
  let photos = {};
  try {
    [sigs, msgs, photos] = await Promise.all([
      fetchPortalSignatures(),
      fetchPortalMessages(),
      fetchPortalPhotos(),
    ]);
  } catch {
    /* portal settings unavailable — skip */
  }

  for (const s of sigs?.[clientId] || []) {
    events.push({
      id: `sig-${s.id}`,
      kind: 'signature',
      title: `${s.signed_name || 'Client'} signed`,
      detail: s.estimate_title || '',
      at: s.signed_at,
    });
  }
  for (const m of msgs?.[clientId] || []) {
    events.push({
      id: `msg-${m.id || m.sent_at}`,
      kind: 'message',
      title: m.from === 'client' ? 'Client message' : 'You replied',
      detail: clip(m.body),
      at: m.sent_at,
    });
  }
  for (const p of photos?.[clientId] || []) {
    events.push({
      id: `photo-${p.id || p.url || p.shared_at}`,
      kind: 'photo',
      title: 'Photo shared',
      detail: p.caption || '',
      at: p.shared_at || p.sharedAt || p.created_at || p.at,
    });
  }

  // Jobs linked to the contact.
  for (const j of findJobsForContact(contact, jobs)) {
    events.push({
      id: `job-${j.id}`,
      kind: 'job',
      title: `Job: ${j.name || j.client || j.address || j.id}`,
      detail: j.phase != null ? `Phase ${j.phase}/5` : j.status || '',
      at: j.started || j.created_at,
    });
  }

  return events
    .filter((e) => e.at)
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
};

/** Compact relative-ish label for a vault timestamp. */
export const vaultTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  try {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};
